import { createTheme } from '@material-ui/core';

export const padding20 = '20px';

export const headerHeight = '50px';
export const pageHeaderHeight = '70px';
export const sectionHeaderHeight = '92px';

export const white = '#ffffff';
export const black = '#000000';

// Text
export const mainTextWhite = '#f4f4f4';
export const mainTextGray = '#c6c6c6';
export const disabledBtnText = '#A6A6A6';

// Main Dark Color
export const blackMainHover = '#2c2c2c';
export const blackMain = '#161616';
export const blackMainLightHover = '#353535';
export const blackMainLight = '#262626';
export const blackCarbon = '#3d3d3d';

// Main Blue Color
export const blueMain = '#0F62FE';
export const blueMainHover = '#0353e9';
export const softBlue = '#d0e2ffd6';

// Main Gray Color
export const grayButton = '#393939';
export const grayButtonHover = '#6f6f6f';

export const red = '#da1e28';
export const redHover = '#ba1b23';
export const green = '#24A148';

// Table
export const tableHeaderBackground = '#e0e0e0';
export const tableHeaderHeight = '30px';
export const tableSelectedRowBackground = '#E8E8E8';
export const tableDisabledRowColor = '#ababab';
export const tableRowHeight = '40px';

// Other
export const navbarDivider = '#393939';
export const pageDescription = '#525252';
export const borderColor = '#000';
export const drawerListTextColor = pageDescription;
export const drawerListSelectedTextColor = blackMain;
export const drawerListHoverColor = '#e5e5e5';
export const cardBackground = '#f3f3f3';

export const warnButtonBg = '#DA1E28';
export const warnButtonHoverBg = '#ba1b23';

export const modalHintBg = '#EDF5FF';
export const modalHintBorder = '#BFD3F7';
export const modalHintBorderHighlight = '#0043CE';

export const warnHintBg = '#fdf6dd';
export const warnHintBorder = '#f8e18f';
export const warnHintBorderHighlight = '#f1c21b';

export const errorHintBg = '#fff1f1';
export const errorHintBorder = '#f09da1';
export const errorHintBorderHighlight = '#da1e28';

export const inputBorder = '#8D8D8D';

export const darkGray = '#E7E7E7';
export const configurationPageBackground = mainTextWhite;
export const fontIBM = '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif';

const defaultSettings = {
  // Typography
  typography: {
    fontFamily: fontIBM,
    color: blackMain,
  },
  // Colors
  palette: {
    primary: {
      main: blueMain,
      dark: blueMainHover,
    },
    secondary: {
      main: grayButton,
      dark: grayButtonHover,
    },
    text: {
      primary: blackMain,
      secondary: mainTextGray,
    },
  },
};

const defaultMuiTheme = createTheme(defaultSettings);

// Overriding of default material UI CSS values

// Tooltips
defaultMuiTheme.overrides.MuiTooltip = {
  tooltip: { borderRadius: 2, backgroundColor: grayButton, fontSize: 12, fontWeight: 300 },
  tooltipPlacementBottom: {
    margin: '10px 0 !important',
  },
};

// Buttons
defaultMuiTheme.overrides.MuiButton = {
  root: {
    textTransform: 'none',
    borderRadius: 0,
  },
  label: {
    fontFamily: fontIBM,
  },
};

//RatioButton
defaultMuiTheme.overrides.MuiRadio = {
  root: {
    color: blackMain,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: blackMain,
    },
  },
};

// Checkbox
defaultMuiTheme.overrides.MuiCheckbox = {
  root: {
    color: blackMain,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: blackMain,
    },
  },
};

// Text Input
defaultMuiTheme.overrides.MuiFilledInput = {
  root: {
    borderRadius: '0 !important',
    backgroundColor: mainTextWhite,
    outline: `2px solid transparent`,
    borderBottom: `1px solid ${inputBorder}`,
    transition: 'outline 70ms cubic-bezier(0.2, 0, 0.38, 0.9)',
    '&:hover': {
      backgroundColor: mainTextWhite,
    },
    '& .MuiFilledInput-input': {
      padding: 5,
      fontSize: 14,
    },
    '&.Mui-focused': {
      backgroundColor: mainTextWhite,
      outline: `2px solid ${blueMain}`,
    },
    '&.MuiFilledInput-multiline': {
      padding: 10,
      backgroundColor: white,
      '&:hover': {
        backgroundColor: white,
      },
    },
  },
  input: {
    '&::-webkit-inner-spin-button': {
      opacity: 1,
    },
    '&::-webkit-outer-spin-button': {
      opacity: 1,
    },
  },
  underline: {
    '&:after': {
      display: 'none',
    },
    '&:before': {
      display: 'none',
    },
  },
};

defaultMuiTheme.overrides.MuiTableRow = {
  root: {
    height: 37,
  },
};

defaultMuiTheme.overrides.MuiTableCell = {
  root: {
    padding: '0 10px',
  },
  head: {
    lineHeight: 1,
  },
};

defaultMuiTheme.overrides.MuiPopover = {
  root: {
    '& .MuiPaper-root .MuiList-padding': {
      padding: 0,
    },
    '& .MuiPaper-rounded': {
      borderRadius: 0,
    },
    '& .MuiPaper-elevation8': {
      boxShadow: '0px 5px 5px -3px rgb(0 0 0 / 4%), 0px 8px 10px 1px rgb(0 0 0 / 4%), 0px 3px 14px 2px rgb(0 0 0 / 4%)',
    },
  },
};

defaultMuiTheme.overrides.MuiIconButton = {
  root: {
    borderRadius: 0,
  },
};

defaultMuiTheme.overrides.MuiDialog = {
  paper: {
    borderRadius: 0,
    backgroundColor: mainTextWhite,
  },
};

defaultMuiTheme.overrides.MuiDialogTitle = {
  root: {
    padding: 0,
    marginBottom: 8,
  },
};

defaultMuiTheme.overrides.MuiDialogContent = {
  root: {
    padding: 16,
  },
};

defaultMuiTheme.overrides.MuiDialogActions = {
  root: {
    height: 64,
    padding: 0,
    marginTop: 8,
    '& button': {
      width: 195,
      height: '100%',
      fontSize: 14,
      fontWeight: 'normal',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      padding: 16,
    },
  },
};

defaultMuiTheme.overrides.MuiAccordion = {
  root: {
    boxShadow: 'none',
    '&:not(:last-child)': {
      marginBottom: 10,
    },
    '&.Mui-expanded': {
      marginTop: 0,
    },
  },
};

export const muiTheme = defaultMuiTheme;
